import { useState, useEffect, useCallback } from 'react';
import {
  getAuth,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User,
  setPersistence,
  browserSessionPersistence,
  browserLocalPersistence
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../utils/firebase';
import { UserProfile } from '../types/user.types';
import { APIResponse } from '../types/survey.types';
import { startSessionTimeout, stopSessionTimeout, sessionManager } from '../utils/sessionManager';

const auth = getAuth();

interface UseAuthReturn {
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  showSessionWarning: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  dismissSessionWarning: () => void;
}

export const useAuth = (): UseAuthReturn => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSessionWarning, setShowSessionWarning] = useState(false);

  // Fetch user profile from Firestore
  const fetchProfile = useCallback(async (uid: string) => {
    try {
      const getUserProfileFn = httpsCallable<{ uid?: string }, APIResponse<UserProfile>>(
        functions,
        'getUserProfile'
      );

      const result = await getUserProfileFn({ uid });

      if (!result.data.success || !result.data.data) {
        throw new Error(result.data.error || 'Failed to fetch user profile');
      }

      setProfile(result.data.data);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching user profile:', err);
      setError(err.message || 'Failed to fetch user profile');
      setProfile(null);
    }
  }, []);

  // Refresh profile manually
  const refreshProfile = useCallback(async () => {
    if (user) {
      await fetchProfile(user.uid);
    }
  }, [user, fetchProfile]);

  // Listen to auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);

      if (firebaseUser) {
        // User is signed in, fetch their profile
        await fetchProfile(firebaseUser.uid);

        // Start session timeout monitoring
        startSessionTimeout(
          () => {
            // onTimeout callback - automatically logout
            console.log('Session timed out due to inactivity');
            setShowSessionWarning(false);
          },
          () => {
            // onWarning callback - show warning modal
            console.log('Session timeout warning - 2 minutes remaining');
            setShowSessionWarning(true);
          }
        );
      } else {
        // User is signed out
        setProfile(null);
        stopSessionTimeout();
      }

      setLoading(false);
    });

    return () => {
      unsubscribe();
      stopSessionTimeout();
    };
  }, [fetchProfile]);

  // Login function
  const login = useCallback(async (email: string, password: string, rememberMe: boolean = false) => {
    setLoading(true);
    setError(null);

    try {
      // Set persistence mode
      await setPersistence(auth, rememberMe ? browserLocalPersistence : browserSessionPersistence);

      // Sign in
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      // Check if email is verified
      if (!userCredential.user.emailVerified) {
        await signOut(auth);
        throw new Error('Please verify your email before logging in. Check your inbox for the verification link.');
      }

      // Fetch user profile
      await fetchProfile(userCredential.user.uid);

      // Check if user is active
      const getUserProfileFn = httpsCallable<{ uid?: string }, APIResponse<UserProfile>>(
        functions,
        'getUserProfile'
      );

      const profileResult = await getUserProfileFn({ uid: userCredential.user.uid });

      if (profileResult.data.success && profileResult.data.data) {
        const userProfile = profileResult.data.data;

        if (!userProfile.isActive) {
          await signOut(auth);
          throw new Error('Your account has been deactivated. Please contact an administrator.');
        }
      }

      // SECURITY FIX: Create server-side session
      await sessionManager.createSession();

      setError(null);
    } catch (err: any) {
      console.error('Login error:', err);

      // Handle specific Firebase auth errors
      let errorMessage = 'Login failed';

      if (err.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email address';
      } else if (err.code === 'auth/wrong-password') {
        errorMessage = 'Incorrect password';
      } else if (err.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
      } else if (err.code === 'auth/user-disabled') {
        errorMessage = 'This account has been disabled';
      } else if (err.code === 'auth/too-many-requests') {
        errorMessage = 'Too many failed login attempts. Please try again later';
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [fetchProfile]);

  // Logout function
  const logout = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      stopSessionTimeout();
      await signOut(auth);
      setUser(null);
      setProfile(null);
      setError(null);
      setShowSessionWarning(false);
    } catch (err: any) {
      console.error('Logout error:', err);
      setError(err.message || 'Logout failed');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Dismiss session warning
  const dismissSessionWarning = useCallback(() => {
    setShowSessionWarning(false);
  }, []);

  // Computed properties
  const isAuthenticated = !!user && !!profile;
  const isAdmin = profile?.role === 'admin' || profile?.role === 'superAdmin';
  const isSuperAdmin = profile?.role === 'superAdmin';

  return {
    user,
    profile,
    loading,
    error,
    isAuthenticated,
    isAdmin,
    isSuperAdmin,
    showSessionWarning,
    login,
    logout,
    refreshProfile,
    dismissSessionWarning,
  };
};
